import { BaseComponent } from './base-component.js';
import { customElement, property } from 'lit/decorators.js';
import { html, css, CSSResultGroup } from 'lit';

@customElement('il-product-section')
export default class ProductSectionComponent extends BaseComponent {
  @property({ type: Array }) images?: string[];
  @property({ type: String }) header?: string;
  @property({ type: String }) body?: string;

  get hasImages(): boolean {
    return (this.images ?? [])?.length > 0;
  }

  override render() {
    return html`
      <il-section
        data-test-iliad-product-section
        .header="${this.header}"
        exportparts="section,section-content"
        class="product-section ${this.hasImages ? '' : 'product-section--no-image'}"
      >
        <div class="product-section__image-container" slot="before">
          ${this.renderImage()}
          <slot name="image-absolute-container"></slot>
        </div>

        <div class="product-section__image-cutoff-container" slot="before">
          ${this.renderImageCutoff()}
        </div>

        <div
          slot="content"
          class="product-section__body"
          data-test-iliad-product-section-body
        >
          <slot name="content">${this.body}</slot>
        </div>

      </il-section>
    `;
  }

  private renderImage() {
    if (!this.images?.length) return html``;

    return html`
      <img
        .src="${this.images?.[0] ?? ''}"
        class="product-section__image"
        data-test-iliad-product-section-image
      />
    `;
  }

  private renderImageCutoff() {
    if (!this.images?.length) return html``;

    return html`
      <img
        .src="${this.images?.[0] ?? ''}"
        class="product-section__image-cutoff"
        data-test-iliad-product-section-image-cutoff
      />
    `;
  }

  static override styles = [
    BaseComponent.styles,
    css`
      .product-section__image-container,
      .product-section__image-cutoff-container {
        width: 100%;
        position: relative;
      }

      .product-section--no-image .product-section__image-container::before,
      .product-section--no-image .product-section__image-cutoff-container::before {
        display: none;
      }

      .product-section__image-container::before,
      .product-section__image-cutoff-container::before {
        content: '';
        display: block;
        padding-top: 66.67%; /* 2/3 = 66.67% for 3:2 aspect ratio */
      }

      /* desktop: sticky positioning for scrolling effect */
      @container (min-width: 640px) {
        .product-section__image-container,
        .product-section__image-cutoff-container {
          position: sticky;
          top: 0;
        }

        .product-section__image-cutoff-container {
          z-index: 5;
          position: absolute;
          top: 0;
          left: 0;
          border-radius: 12px;
          overflow: hidden;
          pointer-events: none;
        }

        .product-section::part(section) {
          overflow: auto;
          scrollbar-width: none;
          gap: 0;
          height: 100%;
        }

        .product-section::part(section-content) {
          background-color: var(--il-color-neutrals-0);
          z-index: 1;
        }
      }

      .product-section__image,
      .product-section__image-cutoff {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
      }

      .product-section__image-cutoff {
        -webkit-mask-image: linear-gradient(
          to bottom,
          black 0,
          black 99px,
          transparent 100px
        );

        mask-image: linear-gradient(
          to bottom,
          black 0,
          black 99px,
          transparent 100px
        );
      }

      /* hide cutoff image on mobile */
      @container (max-width: 639px) {
        .product-section__image-cutoff-container {
          display: none;
        }
      }

      .product-section__body {
        font-size: var(--il-font-size-14);
        font-weight: var(--il-font-weight-normal);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-2);
        color: var(--il-color-neutrals-300);
      }
    `,
  ] as CSSResultGroup;
}
